// API client wrapper — the single choke point for every call to the Sparky REST
// API. It attaches the Cognito ID token to EVERY request (Req 1.3) and, on a 401
// or an unrecoverable session, clears the session and hands control to the
// registered "unauthorized" handler so the app routes back to login (Req 1.5).
//
// Auth header: `Authorization: <ID token>` — the raw JWT, no "Bearer " prefix,
// because the API Gateway COGNITO_USER_POOLS authorizer expects the bare pool
// token. CORS on the API only allows Content-Type + Authorization, so we set
// nothing else.

import { getConfig } from './config.js';
import { getValidIdToken, signOut, NotAuthenticatedError } from './auth.js';

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message || `API error ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// Called whenever the session is no longer usable (missing/expired/refused). The
// app registers this to re-render the login screen.
let _onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  _onUnauthorized = typeof fn === 'function' ? fn : null;
}

function handleUnauthorized() {
  signOut(); // clear any stale tokens
  if (_onUnauthorized) _onUnauthorized();
}

function joinUrl(base, path) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}/${String(path).replace(/^\/+/, '')}`;
}

// Core request. `path` is relative to the configured ApiBaseUrl (e.g. '/health').
// Returns parsed JSON (or null for empty bodies). Throws ApiError on non-2xx and
// NotAuthenticatedError when there is no usable session.
export async function apiFetch(path, options = {}) {
  const { apiBaseUrl } = getConfig();

  let idToken;
  try {
    idToken = await getValidIdToken();
  } catch (err) {
    // No session / refresh failed -> back to login.
    handleUnauthorized();
    throw err instanceof NotAuthenticatedError ? err : new NotAuthenticatedError(String(err));
  }

  const headers = new Headers(options.headers || {});
  headers.set('Authorization', idToken); // ID token, no "Bearer" — see file header
  headers.set('Accept', 'application/json');

  let { body } = options;
  const isPlainObject =
    body != null &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer);
  if (isPlainObject) {
    body = JSON.stringify(body);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(joinUrl(apiBaseUrl, path), { ...options, headers, body });

  // Token rejected by the API (expired/invalid mid-flight) -> re-auth (Req 1.5).
  if (res.status === 401) {
    handleUnauthorized();
    throw new ApiError(401, 'Unauthorized');
  }

  const text = await res.text();
  let parsed = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message = (parsed && parsed.message) || `Request failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed;
}

// Convenience verbs.
export const api = {
  get: (path, options) => apiFetch(path, { ...options, method: 'GET' }),
  post: (path, body, options) => apiFetch(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => apiFetch(path, { ...options, method: 'PUT', body }),
  del: (path, options) => apiFetch(path, { ...options, method: 'DELETE' }),
};
